var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Link } from 'react-router-dom';
import SignInPanel from '../../../components/SignInPanel';
import Alert from '../../../components/Alert';
import Loader from '../../../components/Loader';
import { Input } from '../../../components/Form';
import { AuthContext } from '../../../components/FirebaseAuth';
import { FirebaseAuth, CloudFunctions } from '../../../components/FirebaseAuth/firebase';
import { userSignOut } from '../../../libs/user';
import { normalizePlanSlug, PLAN_LABELS } from '../../../inc/planSlugs';
import { saveSignupDraft, readSignupDraft, clearSignupDraft } from '../../../libs/signup';
/**
 * Public sign-up page (/get-started).
 *
 * /signin reads as a login for people who already have an account, so new
 * customers had no obvious entry point — and even once signed in they had to find
 * "Add Account", name it, and only then reach plan selection. This page collapses
 * that into one screen: name your account, sign in, and land on the plan page with
 * the plan from ?plan= already selected.
 *
 * Sign-in uses a popup, so this component stays mounted throughout and can create
 * the account itself the moment auth succeeds. A sessionStorage draft covers the
 * paths where the page IS torn down (a reload, or a browser forced onto the
 * redirect flow) so nobody loses what they typed.
 */
var CreateAccount = function () {
    var _a;
    var authUser = useContext(AuthContext).authUser;
    var _b = useState({
        hasError: false,
        error: null,
        value: ""
    }), accountName = _b[0], setAccountName = _b[1];
    var _c = useState(null), errorMessage = _c[0], setErrorMessage = _c[1];
    var _d = useState(false), inSubmit = _d[0], setInSubmit = _d[1];
    var _e = useState(false), nameRequired = _e[0], setNameRequired = _e[1];
    // onAuthStateChanged fires as soon as the credential lands, but the panel still
    // has updateProfile + sendEmailVerification to run. Without this the page would
    // swap to the already-signed-in branch mid-flight and flash a stale button.
    var _f = useState(false), authInFlight = _f[0], setAuthInFlight = _f[1];
    // The plan slug is fixed for the life of the page.
    var planSlugRef = useRef(normalizePlanSlug(new URLSearchParams(window.location.search).get('plan')));
    // Mirrors accountName so the sign-in callback can read it without the
    // callback identity changing on every keystroke.
    var nameRef = useRef("");
    // Creation fires at most once per page life. A ref, not state, so it also
    // holds under StrictMode's double-invoked effects.
    var startedRef = useRef(false);
    var planSlug = planSlugRef.current;
    var planLabel = planSlug ? PLAN_LABELS[planSlug] : undefined;
    var isSignedIn = authUser.checked && authUser.user !== null;
    // Until Firebase reports whether anyone is signed in, we don't know which of
    // the two step-2 variants to show. Rendering the sign-in panel speculatively
    // would flash the provider buttons at an already-signed-in visitor.
    var authResolved = authUser.checked;
    // Restore anything typed before a reload.
    useEffect(function () {
        var draft = readSignupDraft();
        if (draft && draft.accountName) {
            nameRef.current = draft.accountName;
            setAccountName({ hasError: false, error: null, value: draft.accountName });
        }
    }, []);
    /**
     * Creates the first account and hands off to plan selection.
     *
     * Reuses an existing unsubscribed account rather than adding another, so a
     * reload or a second visit doesn't leave a trail of empty accounts.
     */
    var createAccountAndGo = useCallback(function () {
        if (startedRef.current) {
            return;
        }
        var draft = readSignupDraft();
        // Consume the draft before the network call so a reload can't replay it.
        clearSignupDraft();
        var name = (nameRef.current || (draft ? draft.accountName : '') || '').trim();
        var slug = planSlugRef.current || (draft ? draft.planSlug : null);
        if (name === '') {
            // They picked a provider before naming the account. They're signed in
            // now, so surface the field and let them finish in one click.
            setNameRequired(true);
            return;
        }
        startedRef.current = true;
        setInSubmit(true);
        setErrorMessage(null);
        setNameRequired(false);
        var goToPlans = function (accountId) {
            // Drop any stale redirect PrivateRoute may have stashed, so it can't
            // hijack the destination.
            localStorage.removeItem('re');
            // A full page load, matching what the Plans page's own submit handlers
            // do: it guarantees AuthProvider has settled before PrivateRoute and
            // AccountTemplate evaluate.
            window.location.assign('/account/' + accountId + '/billing/plan' +
                (slug ? '?plan=' + encodeURIComponent(slug) : ''));
        };
        var uid = authUser.user ? authUser.user.uid : null;
        var findReusableAccount = function () { return __awaiter(void 0, void 0, Promise, function () {
            var snap, reusable_1, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!uid) {
                            return [2 /*return*/, null];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, FirebaseAuth.firestore().collection('accounts')
                                .where('owner', '==', uid).get()];
                    case 2:
                        snap = _a.sent();
                        reusable_1 = null;
                        snap.forEach(function (doc) {
                            var data = doc.data();
                            // No subscriptionStatus means created but never activated.
                            if (reusable_1 === null && !data.subscriptionStatus && data.accessCount !== 0) {
                                reusable_1 = doc.id;
                            }
                        });
                        return [2 /*return*/, reusable_1];
                    case 3:
                        e_1 = _a.sent();
                        // Not worth failing sign-up over; fall through to creating one.
                        return [2 /*return*/, null];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        findReusableAccount().then(function (existingId) {
            if (existingId) {
                goToPlans(existingId);
                return;
            }
            var createAccount = CloudFunctions.httpsCallable('oncall');
            return createAccount({
                name: "createAccount",
                data: {
                    accountName: name,
                    accountType: "provider"
                }
            }).then(function (response) {
                goToPlans(response.data.accountId);
            });
        }).catch(function (err) {
            // The server's message is user-facing (e.g. the account cap text), so
            // show it verbatim. Allow a retry.
            startedRef.current = false;
            setErrorMessage(err.message);
            setInSubmit(false);
        });
    }, [authUser.user]);
    // Stable identity keeps the panel from re-subscribing needlessly.
    var handleSignInSuccess = useCallback(function () {
        createAccountAndGo();
    }, [createAccountAndGo]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "signin-panel signup-panel", children: [_jsxs("div", { className: "signin-panel-body", children: [_jsxs("div", { className: "signin-logo", children: [_jsx("img", { src: "/logo.svg", alt: "LTIAAS logo" }), _jsx("div", { className: "signin-tagline", children: "Customer Portal" })] }), _jsx("h1", { className: "signup-heading", children: "Create your LTIAAS account" }), planLabel &&
                                _jsxs("div", { className: "signup-plan-row", children: [_jsxs("span", { className: "signup-plan-chip", children: ["Plan: ", planLabel] }), _jsx("span", { className: "signup-plan-note", children: "You'll confirm this on the next step." })] }), errorMessage !== null &&
                                _jsx(Alert, { type: "danger", message: errorMessage, dismissible: true, onDismiss: function () { return setErrorMessage(null); } }), inSubmit ? (_jsx(Loader, { text: "Setting up your account..." })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "signup-step", children: [_jsxs("div", { className: "signup-step-head", children: [_jsx("span", { className: "signup-step-num", children: "1" }), _jsx("span", { className: "signup-step-title", children: "Name your account" })] }), _jsx("label", { className: "signup-label", htmlFor: "account-name", children: "Company or organization name" }), _jsx(Input, { type: "text", id: "account-name", name: "account-name", placeholder: "Acme University", maxLen: 100, required: true, defaultValue: accountName.value, hasError: nameRequired, error: nameRequired ? 'Please enter a name for your account.' : null, changeHandler: function (field) {
                                                    nameRef.current = field.value;
                                                    setAccountName(field);
                                                    setNameRequired(false);
                                                    saveSignupDraft(field.value, planSlugRef.current);
                                                } }), _jsx("p", { className: "signup-help", children: "We'll use this to name your first LTIAAS account. Only your team sees it, and you can rename it any time." })] }), _jsxs("div", { className: "signup-step", children: [_jsxs("div", { className: "signup-step-head", children: [_jsx("span", { className: "signup-step-num", children: "2" }), _jsx("span", { className: "signup-step-title", children: isSignedIn ? 'Create your account' : "Choose how you'll sign in" })] }), !authResolved ? (_jsx(Loader, { text: "Just a moment..." })) : (isSignedIn && !authInFlight) ? (_jsxs(_Fragment, { children: [_jsxs("p", { className: "signup-help", children: ["Signed in as ", _jsx("b", { children: (_a = authUser.user) === null || _a === void 0 ? void 0 : _a.email }), ".", ' ', _jsx("button", { type: "button", className: "btn btn-link signup-linkbtn", onClick: function () { return userSignOut(); }, children: "Not you? Sign out" })] }), _jsx("button", { type: "button", className: "btn btn-primary btn-block", disabled: accountName.hasError || inSubmit, onClick: function () { return createAccountAndGo(); }, children: "Create account" }), _jsxs("p", { className: "signup-help mt-2", children: ["This will create an additional account under your login.", ' ', _jsx(Link, { to: "/", children: "Go to your portal" })] })] })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: "signup-help", children: "Use your work Google or Microsoft account, or create a password. We'll create your account as soon as you're signed in." }), _jsx(SignInPanel, { mode: "signup", onSignInStart: function () { return setAuthInFlight(true); }, onSignInSuccess: handleSignInSuccess })] }))] })] }))] }), _jsx("div", { className: "signin-legal text-center", children: _jsxs("em", { children: ["By creating an account, you're agreeing to our", _jsx("a", { href: "https://ltiaas.com/terms", target: "blank", className: "ml-1", children: "terms and conditions" }), ",", _jsx("a", { href: "https://ltiaas.com/service-level-agreement", target: "blank", className: "ml-1", children: "service level agreement" }), ", and", _jsx("a", { href: "https://ltiaas.com/privacy-policy", target: "blank", className: "ml-1", children: "privacy policy" }), "."] }) })] }), _jsx("div", { className: "signin-graphic", "aria-hidden": "true" })] }));
};
export default CreateAccount;
